/**
 * AssignIntegrationsModal Component (Story 4.15, Task 2.1)
 *
 * Modal for quickly assigning existing Google OAuth connections to a client.
 * Shows available Google services grouped by OAuth connection with checkboxes.
 * After assignment, triggers PropertySelector for each new integration.
 */

import { useState, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from "@provenleap/ui";
import { Loader2 } from "lucide-react";
import { apiPost } from "../../lib/apiClient";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import type { ToastType } from "../../hooks/useToast";

// ============================================================================
// Types
// ============================================================================

export interface TenantGoogleConnection {
  oauthTokenId: string;
  connectedPlatforms: string[];
  clientAssignments: Array<{
    clientId: string;
    clientName: string;
    platform: string;
    accountName: string | null;
  }>;
}

interface AssignIntegrationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  connections: TenantGoogleConnection[];
  /** Platforms already connected for this client */
  connectedPlatforms: string[];
  showToast: (type: ToastType, message: string) => void;
  onAssigned: (createdPlatforms: string[]) => void;
}

/** Map platform ID to display name */
const PLATFORM_DISPLAY_NAME = new Map(
  PICKER_PLATFORMS.map((p) => [p.platform, p.displayName])
);

/** Map platform ID to domain for favicon */
const PLATFORM_DOMAIN = new Map(
  PICKER_PLATFORMS.map((p) => [p.platform, p.domain])
);

// ============================================================================
// Component
// ============================================================================

export function AssignIntegrationsModal({
  open,
  onOpenChange,
  clientId,
  clientName,
  connections,
  connectedPlatforms,
  showToast,
  onAssigned,
}: AssignIntegrationsModalProps) {
  const [selectedPlatforms, setSelectedPlatforms] = useState<
    Map<string, Set<string>>
  >(new Map());
  const [assigning, setAssigning] = useState(false);

  // Build available platforms per connection (exclude already-connected for this client)
  const connectedSet = useMemo(() => new Set(connectedPlatforms), [connectedPlatforms]);

  const availableByConnection = useMemo(() => {
    return connections.map((conn) => {
      const available = conn.connectedPlatforms.filter(
        (p) => !connectedSet.has(p)
      );
      return { ...conn, availablePlatforms: available };
    }).filter((conn) => conn.availablePlatforms.length > 0);
  }, [connections, connectedSet]);

  const totalSelected = useMemo(() => {
    let count = 0;
    for (const platforms of selectedPlatforms.values()) {
      count += platforms.size;
    }
    return count;
  }, [selectedPlatforms]);

  const handleToggle = useCallback(
    (oauthTokenId: string, platform: string) => {
      setSelectedPlatforms((prev) => {
        const next = new Map(prev);
        const current = next.get(oauthTokenId) ?? new Set<string>();
        const updated = new Set(current);
        if (updated.has(platform)) {
          updated.delete(platform);
        } else {
          updated.add(platform);
        }
        if (updated.size === 0) {
          next.delete(oauthTokenId);
        } else {
          next.set(oauthTokenId, updated);
        }
        return next;
      });
    },
    []
  );

  const handleAssign = useCallback(async () => {
    if (totalSelected === 0) return;
    setAssigning(true);

    const allCreated: string[] = [];

    try {
      // Process each OAuth token's selected platforms
      for (const [oauthTokenId, platforms] of selectedPlatforms) {
        if (platforms.size === 0) continue;

        const res = await apiPost(
          `/api/clients/${clientId}/integrations/assign`,
          {
            oauthTokenId,
            platforms: [...platforms],
          }
        );

        if (!res.ok) {
          const errorJson = await res.json().catch(() => null);
          const detail =
            (errorJson as { detail?: string })?.detail ?? "Failed to assign integrations";
          showToast("error", detail);
          continue;
        }

        const json = (await res.json()) as {
          data: { created: string[]; existing: string[] };
        };
        allCreated.push(...json.data.created);
      }

      if (allCreated.length > 0) {
        const names = allCreated
          .map((p) => PLATFORM_DISPLAY_NAME.get(p) ?? p)
          .join(", ");
        showToast("success", `Assigned ${names} to ${clientName}`);
        onAssigned(allCreated);
      } else {
        showToast("info", "All selected integrations were already connected");
      }

      onOpenChange(false);
      setSelectedPlatforms(new Map());
    } catch {
      showToast("error", "Failed to assign integrations. Please try again.");
    } finally {
      setAssigning(false);
    }
  }, [
    totalSelected,
    selectedPlatforms,
    clientId,
    clientName,
    showToast,
    onAssigned,
    onOpenChange,
  ]);

  // Reset selection when dialog opens/closes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSelectedPlatforms(new Map());
      }
      onOpenChange(isOpen);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Use Existing Google Connection</DialogTitle>
          <DialogDescription>
            Select Google services to assign to {clientName}. You can pick
            properties after assignment.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 max-h-80 overflow-y-auto space-y-4">
          {availableByConnection.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No additional Google services available to assign.
            </p>
          )}

          {availableByConnection.map((conn) => {
            // Show which clients already use this connection for context
            const existingClients = [
              ...new Set(conn.clientAssignments.map((a) => a.clientName)),
            ];

            return (
              <div key={conn.oauthTokenId} className="space-y-2">
                {connections.length > 1 && (
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Google Account ({existingClients.join(", ")})
                  </p>
                )}
                <div className="space-y-1">
                  {conn.availablePlatforms.map((platform) => {
                    const isSelected =
                      selectedPlatforms.get(conn.oauthTokenId)?.has(platform) ??
                      false;
                    const domain = PLATFORM_DOMAIN.get(platform);

                    return (
                      <label
                        key={platform}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer transition-colors hover:bg-accent ${
                          isSelected
                            ? "border-primary bg-accent"
                            : "border-transparent"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            handleToggle(conn.oauthTokenId, platform)
                          }
                          disabled={assigning}
                          className="h-4 w-4 accent-primary"
                        />
                        {domain && (
                          <img
                            src={logoUrl(domain)}
                            alt=""
                            width={20}
                            height={20}
                            className="shrink-0 rounded-sm"
                          />
                        )}
                        <span className="text-sm font-medium">
                          {PLATFORM_DISPLAY_NAME.get(platform) ?? platform}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={assigning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={totalSelected === 0 || assigning}
          >
            {assigning ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Assigning...
              </>
            ) : (
              `Assign ${totalSelected > 0 ? `(${totalSelected})` : ""}`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
